function toBase64UrlUint8Array(base64UrlString) {
    const padding = "=".repeat((4 - (base64UrlString.length % 4)) % 4);
    const base64 = (base64UrlString + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const outputArray = new Uint8Array(raw.length);

    for (let index = 0; index < raw.length; index += 1) {
        outputArray[index] = raw.charCodeAt(index);
    }

    return outputArray;
}

const DEFAULT_SW_TIMEOUT_MS = 1200;

function withTimeout(promise, timeoutMs, fallbackValue) {
    return Promise.race([
        promise,
        new Promise((resolve) => {
            window.setTimeout(() => resolve(fallbackValue), timeoutMs);
        }),
    ]);
}

function isSupported() {
    return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
}

async function registerServiceWorker() {
    if (!isSupported()) return null;
    const registration = await navigator.serviceWorker.register("/sw.js");
    return registration;
}

async function ensurePermission() {
    if (!isSupported()) return "denied";

    if (Notification.permission === "granted") {
        return "granted";
    }

    const permission = await Notification.requestPermission();
    return permission;
}

async function subscribe({ vapidPublicKey }) {
    if (!isSupported()) return null;
    if (!vapidPublicKey) return null;

    const registration = await registerServiceWorker();
    if (!registration) return null;

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
        return existing;
    }

    return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toBase64UrlUint8Array(vapidPublicKey),
    });
}

async function getServiceWorkerRegistration({ timeoutMs = DEFAULT_SW_TIMEOUT_MS } = {}) {
    if (!isSupported()) return null;

    try {
        const registration = await navigator.serviceWorker.getRegistration("/sw.js");
        if (registration) return registration;
    } catch (_err) {
        // Fall through to additional lookup strategies.
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) return registration;
    } catch (_err) {
        // Fall through to ready() fallback.
    }

    try {
        const readyRegistration = await withTimeout(navigator.serviceWorker.ready, timeoutMs, null);
        return readyRegistration || null;
    } catch (_err) {
        return null;
    }
}

async function getCurrentSubscription({ timeoutMs = DEFAULT_SW_TIMEOUT_MS } = {}) {
    if (!isSupported()) return null;
    const registration = await getServiceWorkerRegistration({ timeoutMs });
    if (!registration) return null;

    try {
        return await withTimeout(registration.pushManager.getSubscription(), timeoutMs, null);
    } catch (_err) {
        return null;
    }
}

async function unsubscribeCurrent({ timeoutMs = DEFAULT_SW_TIMEOUT_MS } = {}) {
    const subscription = await getCurrentSubscription({ timeoutMs });
    if (!subscription) return false;

    try {
        return await withTimeout(subscription.unsubscribe(), timeoutMs, false);
    } catch (_err) {
        return false;
    }
}

export { ensurePermission, getCurrentSubscription, isSupported, subscribe, unsubscribeCurrent };
